import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import Clickable from 'components/Common/Clickable'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { nonNullable } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;
`

const CustomDivider = styled(Divider)`
  && {
    background-color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }
`

interface Props {
  onLinkClick: () => void
  config: App.LoyaltyConfig
}

function AccountMenuLoyaltyNoAccountSilverTile({ onLinkClick, config }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const handleActivateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    onLinkClick()
  }, [dispatch, onLinkClick])

  const { regionTierBenefits } = config

  const silverLabel = intl.formatMessage({
    id: '4B3oSXmHKn',
    defaultMessage: 'Silver',
    description: 'Silver tier label',
  })

  const exclusiveInviteLabel = intl.formatMessage({
    id: 'nnkFAhRmGM',
    defaultMessage: 'EXCLUSIVE INVITE',
    description: 'Exclusive invite label for loyalty tier',
  })

  const unlockedMessage = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'Q+CTUV1cnp',
        defaultMessage: 'You\'ve unlocked <silverStatus>Silver status</silverStatus>',
        description: 'Message indicating user has unlocked Silver status',
      },
      {
        silverStatus: (chunks: React.ReactNode) => (
          <Colour key="silver-status" value="tier-silver-light-contrast">
            {chunks}
          </Colour>
        ),
      },
    )
  }, [intl])

  const benefitsReadyMessage = intl.formatMessage({
    id: 'oxuyjFnDwX',
    defaultMessage: 'Your benefits, ready when you are',
    description: 'Message about benefits being ready',
  })

  const freeRoomUpgradeLabel = intl.formatMessage({
    id: '5xsIEs7fqi',
    defaultMessage: 'Free room or package upgrade',
    description: 'Label for free room or package upgrade benefit',
  })

  const freeAirportLoungeLabel = intl.formatMessage({
    id: 'W8I4ROkGoi',
    defaultMessage: 'Free airport lounge passes',
    description: 'Label for free airport lounge passes benefit',
  })

  const discountedInsuranceLabel = intl.formatMessage({
    id: '1FrEKQH1DO',
    defaultMessage: 'Discounted travel insurance',
    description: 'Label for discounted travel insurance benefit',
  })

  const priorityPhoneSupportLabel = intl.formatMessage({
    id: 'KJ02UJasBB',
    defaultMessage: 'Priority phone support 24/7',
    description: 'Label for priority phone support benefit',
  })

  const activateButtonLabel = intl.formatMessage({
    id: 'GPQ6ShiZnR',
    defaultMessage: 'Activate free membership',
    description: 'Button label to activate free membership',
  })

  const unlimitedLabel = intl.formatMessage({
    id: 'WPhXBTjs3i',
    defaultMessage: 'Unlimited',
    description: 'Label for unlimited loyalty benefit',
  })

  const formatBenefitLimit = useCallback((benefit: App.LoyaltyRegionTierBenefit): string => {
    if (benefit.unlimited) {
      return unlimitedLabel
    }
    return intl.formatMessage(
      {
        id: 'P7ZrdWvVOw',
        defaultMessage: '{count}x',
        description: 'Benefit limit with count',
      },
      { count: benefit.limit },
    )
  }, [intl, unlimitedLabel])

  const formatBenefitPercentage = useCallback((amount: number): string => {
    return intl.formatMessage(
      {
        id: 'rqC25CEs1H',
        defaultMessage: '{amount}%',
        description: 'Benefit percentage discount',
      },
      { amount },
    )
  }, [intl])

  const benefits = useMemo(() => {
    const orderedBenefits: Array<App.LoyaltyBenefitType> = [
      'hotel_upgrade',
      'lounge_pass',
      'discounted_insurance',
      'phone_support',
    ]

    return nonNullable(orderedBenefits.map(benefitType => (
      regionTierBenefits.silver.find(benefit => benefit.code === benefitType)
    )))
  }, [regionTierBenefits])

  return <Container to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLinkClick}>
    <VerticalSpacer gap={20}>
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LoyaltyProgramNameLogo width={72} />
          <BodyText variant="medium" format="uppercase">{silverLabel}</BodyText>
        </Group>
        <LoyaltyTierLabel
          tier="silver"
          kind="primary"
          format="uppercase"
        >
          {exclusiveInviteLabel}
        </LoyaltyTierLabel>
        <Heading variant="heading4">
          {unlockedMessage}
        </Heading>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">
          {benefitsReadyMessage}
        </BodyText>
        {benefits.map((benefit, idx) => (
          <React.Fragment key={benefit.code}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              {benefit.code === 'hotel_upgrade' && <>
                <BodyTextBlock variant="small" startIcon={<LineCrownIcon />}>
                  {freeRoomUpgradeLabel}
                </BodyTextBlock>
                <Group direction="horizontal" verticalAlign="center" gap={12}>
                  <LoyaltyTierLabel kind="tertiary" tier="silver">
                    {formatBenefitLimit(benefit)}
                  </LoyaltyTierLabel>
                </Group>
              </>}
              {benefit.code === 'lounge_pass' && <>
                <BodyTextBlock variant="small" startIcon={<LineChairWithAirplaneIcon />}>
                  {freeAirportLoungeLabel}
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="silver">
                  {formatBenefitLimit(benefit)}
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'discounted_insurance' && <>
                <BodyTextBlock variant="small" startIcon={<LineShieldCheckIcon />}>
                  {discountedInsuranceLabel}
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="silver">
                  {benefit.value?.amount ? formatBenefitPercentage(benefit.value.amount) : ''}
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'phone_support' && <BodyTextBlock variant="small" startIcon={<LinePhoneIcon />}>
                {priorityPhoneSupportLabel}
              </BodyTextBlock>}
            </Group>
            {idx < benefits.length - 1 && <CustomDivider kind="primary" />}
          </React.Fragment>
        ))}
      </VerticalSpacer>
      <TextButton kind="primary" size="medium" fit="full-width" onClick={handleActivateMembership}>
        {activateButtonLabel}
      </TextButton>
    </VerticalSpacer>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyNoAccountSilverTile)
